import { useEffect, useState } from 'react'
import { getSupabaseClient } from '../lib/supabase'
import type { BookingSettings } from '../types/booking'

const DEFAULT_SETTINGS: BookingSettings = {
  pricePerPerson: 10_000,
  maxGuests: 5,
  closedDates: [],
  openingHours: {
    thursday: { enabled: true, intervals: [{ start: '16:00', end: '19:00' }] },
    friday: { enabled: true, intervals: [{ start: '16:00', end: '19:00' }] },
    saturday: { enabled: true, intervals: [{ start: '16:00', end: '19:00' }] },
  },
}

interface OpeningHoursDay {
  enabled: boolean
  intervals: Array<{ start: string; end: string }>
}

interface OpeningHours {
  [day: string]: OpeningHoursDay
}

export const useSettings = () => {
  const [settings, setSettings] = useState<BookingSettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadSettings = async () => {
      console.log('🔄 [WIDGET] Loading settings from database...')

      try {
        const supabase = getSupabaseClient()
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['opening_hours', 'closed_dates', 'default_capacity', 'default_price'])

        if (error) throw error

        console.log('📥 [WIDGET] Settings loaded:', data)

        const loadedSettings: Partial<BookingSettings> = {}

        data?.forEach((row) => {
          switch (row.key) {
            case 'opening_hours': {
              let value = row.value
              if (typeof value === 'string') {
                try {
                  value = JSON.parse(value)
                } catch (parseError) {
                  console.error('❌ [WIDGET] Failed to parse opening hours payload', parseError)
                  value = null
                }
              }
              if (value && typeof value === 'object') {
                loadedSettings.openingHours = value as OpeningHours
              }
              break
            }
            case 'closed_dates': {
              let value = row.value
              if (typeof value === 'string') {
                try {
                  value = JSON.parse(value)
                } catch (parseError) {
                  console.error('❌ [WIDGET] Failed to parse closed dates payload', parseError)
                  value = null
                }
              }
              loadedSettings.closedDates = Array.isArray(value) ? (value as string[]) : []
              break
            }
            case 'default_capacity': {
              const value =
                typeof row.value === 'string' ? Number(row.value) : typeof row.value === 'number' ? row.value : undefined
              if (typeof value === 'number' && !Number.isNaN(value)) {
                loadedSettings.maxGuests = value
              }
              break
            }
            case 'default_price': {
              const value =
                typeof row.value === 'string' ? Number(row.value) : typeof row.value === 'number' ? row.value : undefined
              if (typeof value === 'number' && !Number.isNaN(value)) {
                loadedSettings.pricePerPerson = value
              }
              break
            }
            default:
              break
          }
        })

        const finalSettings = { ...DEFAULT_SETTINGS, ...loadedSettings }
        console.log('✅ [WIDGET] Final settings:', finalSettings)
        setSettings(finalSettings)
      } catch (error) {
        console.error('❌ [WIDGET] Failed to load settings:', error)
      } finally {
        setLoading(false)
      }
    }

    loadSettings()
  }, [])

  return { settings, loading }
}

